/**
 * @file sharingLine.ts
 * @brief The words of a meeting card's "Shared with / Shared by" line.
 *
 * @details
 * Sharing v2 (DES 21-10-2026, decision 6). From the list item's `sharing`:
 * - owner / admin with people: "Shared with Sara, Bob +2", an External
 *   person marked "(Ext)" (the setup's "Ext"). None when not shared.
 * - shared / invited / viewer: "Shared by Ali".
 * - null / absent (an older server, or the server could not read it): none.
 *
 * Pure and free of app imports, so `node --test` can load it directly.
 *
 * @references
 * - ./MeetingSharingLine.tsx
 * - scratch/claude-code-report.md "Sharing v2: Part 1" section 2
 *
 * @author DES
 * @date 21-10-2026
 */

export type SharingPerson = {
  userId?: string | null;
  firstName?: string | null;
  avatarUrl?: string | null;
  isExternal?: boolean | null;
};

export type SharingSummary = {
  sharedCount?: number | null;
  people?: SharingPerson[] | null;
  viewerRole?: string | null;
  sharedBy?: { firstName?: string | null; name?: string | null } | null;
  sharedAt?: string | null;
};

export type SharingLine =
  | { kind: "sharedWith"; text: string; tooltip: string; people: SharingPerson[] }
  | { kind: "sharedBy"; text: string; tooltip: string };

const personLabel = (p: SharingPerson) => {
  const name = (p.firstName ?? "").trim() || "Someone";
  return p.isExternal ? `${name} (Ext)` : name;
};

export function sharingLineText(s: SharingSummary | null | undefined): SharingLine | null {
  if (!s) return null;
  const role = s.viewerRole ?? "";
  if (role === "owner" || role === "admin") {
    const count = Math.max(0, Number(s.sharedCount ?? 0));
    if (!count) return null;
    const people = (s.people ?? []).slice(0, 3);
    const extra = count - people.length;
    const names = people.map(personLabel);
    const text = names.length
      ? `Shared with ${names.join(", ")}${extra > 0 ? ` +${extra}` : ""}`
      : `Shared with ${count} ${count === 1 ? "person" : "people"}`;
    const owner = role === "admin" ? (s.sharedBy?.name || s.sharedBy?.firstName || "").trim() : "";
    return {
      kind: "sharedWith",
      text,
      tooltip: owner ? `${text}. Owner: ${owner}` : text,
      people,
    };
  }
  const by = (s.sharedBy?.firstName || s.sharedBy?.name || "").trim();
  if (!by) return null;
  const full = (s.sharedBy?.name || by).trim();
  const when = s.sharedAt ? new Date(s.sharedAt) : null;
  const whenText =
    when && !Number.isNaN(when.getTime())
      ? ` on ${when.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`
      : "";
  return { kind: "sharedBy", text: `Shared by ${by}`, tooltip: `Shared by ${full}${whenText}` };
}

/** What a card's "⋯" menu offers (DES 22-10-2026): the owner shares and
 *  sees who has it; a company admin on a member's meeting sees (and may
 *  remove); anyone else neither (GET /access is 404 for them). */
export function meetingShareRights(s: SharingSummary | null | undefined): {
  canShare: boolean;
  canSeeAccess: boolean;
} {
  const role = s?.viewerRole ?? "";
  return { canShare: role === "owner", canSeeAccess: role === "owner" || role === "admin" };
}
